import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import ElementsPanel from '../studio/elements/ElementsPanel';
import { ElementFolderModal } from '../studio/elements';
import { CTAModal } from '../studio/cta';
import { PollModal } from '../studio/poll';
import { TimerElementModal } from '../studio/timer';
import { VideoElementModal } from '../studio/video';
import { AudioElementModal } from '../studio/audio';
import { PrompterElementModal } from '../studio/prompter';
import { WebOverlayModal } from '../studio/weboverlay';
import { TalkingPointModal } from '../studio/talkingpoint';
import { LeadCaptureModal } from '../studio/leadcapture';
import { ProductShowcaseModal } from '../studio/product';
import { CheckoutModal } from '../studio/checkout';
import { AuctionModal } from '../studio/auction';
import { ContractModal } from '../studio/contract';
import { PresentationElementModal } from '../studio/presentation';
import { AssetLibraryModal } from '../studio/assets';
import CopyToMyElementsModal from '../elements/CopyToMyElementsModal';
// Team / Users share modal (different from the share-by-code modal in ../elements/ShareElementModal)
import ShareWithTeamModal from './ShareElementModal';
import CloneAllSharedElementsModal from '../elements/CloneAllSharedElementsModal';
import ShareElementModal from '../elements/ShareElementModal';
import ShareFolderModal from '../elements/ShareFolderModal';
import GetSharedElementModal from '../elements/GetSharedElementModal';
import folderService from '../../services/folderServices';
import requests from '../../services/httpServices';
import { useSiteContext } from '../../context/SiteContext';

const BASIC_PLAN_TYPES = ['video', 'audio', 'presentation'];

// Normalize element type to match modal type expectations
const normalizeElementType = (type) => {
  const typeMap = {
    'talkingpoint': 'talking_point',
    'talking_point': 'talking_point',
    'weboverlay': 'web_overlay',
    'web_overlay': 'web_overlay',
    'leadcapture': 'lead_capture',
    'lead_capture': 'lead_capture',
    'productshowcase': 'product_showcase',
    'product_showcase': 'product_showcase',
    'product': 'product_showcase',
    'teleprompter': 'prompter',
    'prompter': 'prompter',
  };
  return typeMap[type] || type;
};

export default function ElementsTab() {
  const { user } = useSiteContext();
  const isBasicPlan = user?.plan !== 'Business';

  // Returns the copy handler unless the element type is gated for basic plan users
  const getCopyHandler = (element) => {
    if (!element) return undefined;
    const type = normalizeElementType(element.type);
    if (isBasicPlan && !BASIC_PLAN_TYPES.includes(type)) return undefined;
    return () => handleOpenCopyModal(element);
  };

  // State for folders and elements
  const [folders, setFolders] = useState([]);
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rawFolders, setRawFolders] = useState([]);

  // Shared library state
  const [sharedFolders, setSharedFolders] = useState([]);

  // Modal states
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [editingElement, setEditingElement] = useState(null);
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [copyingElement, setCopyingElement] = useState(null);
  const [shareElementTarget, setShareElementTarget] = useState(null);
  const [cloningSharedFolder, setCloningSharedFolder] = useState(null);
  const [sharingElement, setSharingElement] = useState(null);
  const [sharingFolder, setSharingFolder] = useState(null);
  const [showGetSharedModal, setShowGetSharedModal] = useState(false);
  const [focusFolderId, setFocusFolderId] = useState(null);

  // Load folders and their elements
  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // First get all folders
      const foldersResponse = await folderService.getFolders();
      const foldersList = Array.isArray(foldersResponse) ? foldersResponse : [];
      setRawFolders(foldersList);

      // Transform folders for ElementsPanel
      const transformedFolders = foldersList.map(folder => ({
        id: folder._id,
        name: folder.name,
        color: folder.color || '#6a1fbf',
        order: folder.order || 0,
      }));
      setFolders(transformedFolders);

      // Now fetch elements for each folder
      const allElements = [];
      for (const folder of foldersList) {
        try {
          const elementsResponse = await folderService.getCtaByFolderId(folder._id);
          const folderElements = Array.isArray(elementsResponse) ? elementsResponse : [];

          // Transform elements for ElementsPanel
          folderElements.forEach(el => {
            let parsedSettings = {};
            if (el.settings) {
              try {
                parsedSettings = typeof el.settings === 'string' ? JSON.parse(el.settings) : el.settings;
              } catch (e) {
                console.error('Failed to parse element settings:', e);
              }
            }

            allElements.push({
              id: el._id,
              name: el.name,
              type: el.type,
              folder_id: folder._id,
              displayStyle: el.displayStyle,
              data: {
                ...el,
                ...parsedSettings,
                _id: el._id,
                name: el.name,
                type: el.type,
                folderId: folder._id,
              },
              is_favorite: el.is_favorite || false,
              isDefault: el.isDefault || false,
              order: el.sortorder || el.order || 0,
            });
          });
        } catch (err) {
          console.error(`Failed to fetch elements for folder ${folder._id}:`, err);
        }
      }

      setElements(allElements);

      // Fetch shared libraries (non-blocking — don't fail the whole load if this fails)
      try {
        const sharedRes = await requests.get('/element/shared-library');
        const fetchedShared = (sharedRes.sharedFolders || [])
          // Filter out entries the user owns (they already see those in their own folders)
          .filter(f => !f.isOwner)
          .map(folder => ({
            id: folder._id,
            name: folder.name,
            color: folder.color || '#6a1fbf',
            shared: true,
            readOnly: true,
            ownerName: folder.ownerName,
            sharedLibraryId: folder.sharedLibraryId,
            elements: (folder.Elementss || []).map(el => ({
              id: el._id,
              name: el.name,
              type: el.type,
              folder_id: folder._id,
              displayStyle: el.displayStyle,
              shared: true,
              readOnly: true,
              sharedLibraryId: folder.sharedLibraryId,
              is_favorite: false,
              order: el.sortorder || 0,
              data: { ...el, _id: el._id, name: el.name, type: el.type, folderId: folder._id },
            })),
          }));
        setSharedFolders(fetchedShared);
      } catch (sharedErr) {
        // Not fatal — user may not have access to any shared libraries
        setSharedFolders([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load elements');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Folder handlers
  const handleCreateFolder = () => {
    setEditingFolder(null);
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setShowFolderModal(true);
  };

  const handleSaveFolder = async (folderData) => {
    try {
      if (editingFolder) {
        await folderService.updateFolder(editingFolder.id, folderData);
        toast.success('Folder updated');
      } else {
        await folderService.createFolder(folderData);
        toast.success('Folder created');
      }
      setShowFolderModal(false);
      setEditingFolder(null);
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save folder');
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}" and all its elements?`)) return;
    try {
      await folderService.deleteFolder(folder.id);
      toast.success('Folder deleted');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete folder');
    }
  };

  // Element handlers
  const handleCreateElement = (type) => {
    const defaultFolder = folders[0];
    if (!defaultFolder) {
      toast.error('Please create a folder first');
      return;
    }

    setEditingElement({
      type,
      folder_id: defaultFolder.id,
      name: `New ${type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
      data: {},
    });
    setModalType(type);
  };

  const handleEditElement = async (element) => {
    // For shared elements, always fetch fresh data from the source so
    // any changes the library owner made are immediately visible.
    if (element.readOnly || element.shared) {
      try {
        const res = await requests.get(`/element/${element.id}`);
        if (res?.element) {
          const fresh = res.element;
          const freshData = {
            ...fresh,
            ...fresh.settings,
            _id: fresh._id,
            name: fresh.name,
            type: fresh.type,
            folderId: element.folder_id,
          };
          setEditingElement({ ...element, data: freshData });
          setModalType(normalizeElementType(element.type));
          return;
        }
      } catch (_) {
        // Fall through to use cached data on error
      }
    }
    setEditingElement(element);
    setModalType(normalizeElementType(element.type));
  };

  const handleOpenCopyModal = (element) => {
    // Close the edit modal so only the copy modal is visible
    setEditingElement(null);
    setModalType(null);
    setCopyingElement(element);
  };

  const handleCloseCopyModal = () => {
    setCopyingElement(null);
  };

  const handleCloneAllSharedElements = (folder) => {
    setCloningSharedFolder(folder);
  };

  const handleCloseCloneAllModal = () => {
    setCloningSharedFolder(null);
  };

  const handleShareElement = (element) => {
    setSharingElement(element);
  };

  const handleCloseShareModal = () => {
    setSharingElement(null);
  };

  const handleShareFolder = (folder) => {
    setSharingFolder(folder);
  };

  const handleCloseShareFolderModal = () => {
    setSharingFolder(null);
  };

  const handleOpenGetShared = () => {
    setShowGetSharedModal(true);
  };

  const handleCloseGetShared = () => {
    setShowGetSharedModal(false);
  };

  const handleGetSharedSuccess = (folderId) => {
    setFocusFolderId(folderId);
    loadData();
  };

  // Get the original element type for saving (before normalization)
  const getOriginalElementType = (normalizedType) => {
    const reverseMap = {
      'talking_point': 'talkingpoint',
      'web_overlay': 'weboverlay',
      'lead_capture': 'leadcapture',
      'product_showcase': 'productshowcase',
    };
    return reverseMap[normalizedType] || normalizedType;
  };

  const handleSaveElement = async (data) => {
    try {
      const elementType = getOriginalElementType(modalType);
      const folderId = data.folder_id || editingElement?.folder_id || folders[0]?.id;

      if (editingElement?.id) {
        // Update existing element - use folderService like meeting page
        await folderService.updateElement(editingElement.id, {
          ...data,
          type: elementType,
        });
        toast.success('Element updated');
      } else {
        // Create new element - use folderService like meeting page
        await folderService.createElement(folderId, {
          ...data,
          type: elementType,
        });
        toast.success('Element created');
      }
      setEditingElement(null);
      setModalType(null);
      // Reload data to refresh the list
      await loadData();
    } catch (error) {
      console.error('Error saving element:', error);
      toast.error(error.message || 'Failed to save element');
    }
  };

  const handleCloseModal = () => {
    setEditingElement(null);
    setModalType(null);
  };

  const handleDuplicateElement = async (element) => {
    try {
      await requests.post('/element/duplicate', { id: element.id });
      toast.success('Element duplicated');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to duplicate element');
    }
  };

  const handleDeleteElement = async (element) => {
    if (!window.confirm(`Delete element "${element.name}"?`)) return;
    try {
      await requests.post('/element/delete', { id: element.id });
      toast.success('Element deleted');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete element');
    }
  };

  const handleMoveElement = async (element, newFolderId) => {
    try {
      await requests.post('/element/update-element', {
        id: element.id,
        folderId: newFolderId,
      });
      toast.success('Element moved');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to move element');
    }
  };

  const handleToggleFavorite = async (element) => {
    try {
      await requests.post('/element/favorite', {
        id: element.id,
        is_favorite: !element.is_favorite,
      });
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update favorite');
    }
  };

  const handleSetDefault = async (element) => {
    try {
      await requests.post('/element/set-default-showcase', {
        id: element.id,
        isDefault: !element.isDefault,
      });
      toast.success(element.isDefault ? 'Removed default' : 'Set as default');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update default');
    }
  };

  const handleRenameElement = async (element, newName) => {
    try {
      await requests.post('/element/update-element', {
        id: element.id,
        name: newName,
      });
      toast.success('Element renamed');
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to rename element');
    }
  };

  const handleReorderElements = async (reorderedElements) => {
    try {
      await requests.post('/element/reorder', { elements: reorderedElements });
      await loadData();
    } catch (error) {
      console.error('Failed to reorder elements:', error);
    }
  };

  // When clicking an element, open edit modal
  const handleElementClick = (element) => {
    handleEditElement(element);
  };

  if (loading && folders.length === 0) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6a1fbf]"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 h-full">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Elements Library</h2>
        <p className="text-gray-400">
          Pre-create and organize elements that you can use across all your sessions
        </p>
      </div>

      {/* Elements Panel */}
      <div className="bg-white/5 rounded-xl border border-white/10 h-[calc(100vh-250px)] overflow-hidden">
        <ElementsPanel
          folders={folders}
          elements={elements}
          sharedFolders={sharedFolders}
          activeElements={{}}
          onActivateElement={handleElementClick}
          onDeactivateElement={() => {}}
          onCreateFolder={handleCreateFolder}
          onEditFolder={handleEditFolder}
          onDeleteFolder={handleDeleteFolder}
          onCreateElement={handleCreateElement}
          onEditElement={handleEditElement}
          onDuplicateElement={handleDuplicateElement}
          onDeleteElement={handleDeleteElement}
          onMoveElement={handleMoveElement}
          onToggleFavorite={handleToggleFavorite}
          onSetDefault={handleSetDefault}
          hiddenTypesExtra={!user?.isAdmin ? ['productshowcase', 'product_showcase', 'auction'] : []}
          onPreviewVideo={handleEditElement}
          onPreviewAudio={handleEditElement}
          onPreviewPresentation={handleEditElement}
          onSharePresentation={() => {}}
          onRenameElement={handleRenameElement}
          onReorderElements={handleReorderElements}
          onOpenAssetLibrary={() => setShowAssetLibrary(true)}
          onCopySharedElement={handleOpenCopyModal}
          onCloneAllSharedElements={handleCloneAllSharedElements}
          onShareWithTeam={(el) => setShareElementTarget(el)}
          onShareElement={handleShareElement}
          onShareFolder={handleShareFolder}
          onGetSharedElement={handleOpenGetShared}
          focusFolderId={focusFolderId}
          onFocusFolderHandled={() => setFocusFolderId(null)}
        />
      </div>

      {/* Share with Team Modal */}
      {shareElementTarget && (
        <ShareWithTeamModal
          isOpen={!!shareElementTarget}
          element={shareElementTarget}
          onClose={() => setShareElementTarget(null)}
          onSaved={(sharedWith) => {
            const targetId = String(shareElementTarget._id || shareElementTarget.id);
            setElements((prev) =>
              prev.map((el) =>
                String(el._id || el.id || el.data?._id) === targetId
                  ? { ...el, sharedWith, data: { ...el.data, sharedWith } }
                  : el
              )
            );
          }}
        />
      )}

      {/* Folder Modal */}
      {showFolderModal && (
        <ElementFolderModal
          isOpen={showFolderModal}
          onClose={() => {
            setShowFolderModal(false);
            setEditingFolder(null);
          }}
          onSave={handleSaveFolder}
          folder={editingFolder}
        />
      )}

      {/* Element Configuration Modals */}
      {modalType === 'cta' && (
        <CTAModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          cta={editingElement?.data}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'talking_point' && (
        <TalkingPointModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          talkingPoint={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'web_overlay' && (
        <WebOverlayModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          overlay={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'poll' && (
        <PollModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          poll={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'video' && (
        <VideoElementModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          video={editingElement?.data}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'audio' && (
        <AudioElementModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          audio={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'timer' && (
        <TimerElementModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          timer={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'presentation' && (
        <PresentationElementModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          presentation={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'prompter' && (
        <PrompterElementModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          prompter={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'product_showcase' && (
        <ProductShowcaseModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          showcase={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'checkout' && (
        <CheckoutModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          checkout={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'lead_capture' && (
        <LeadCaptureModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          leadCapture={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'auction' && (
        <AuctionModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          auction={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {modalType === 'contract' && (
        <ContractModal
          isOpen={true}
          onClose={handleCloseModal}
          onSave={handleSaveElement}
          contract={editingElement?.data ? { ...editingElement.data, folder_id: editingElement.folder_id } : null}
          folders={folders}
          currentFolder={folders?.find(f => f.id === editingElement?.folder_id)}
          readOnly={editingElement?.readOnly === true}
          onCopyToMyElements={getCopyHandler(editingElement)}
        />
      )}

      {/* Asset Library Modal */}
      {showAssetLibrary && (
        <AssetLibraryModal
          isOpen={showAssetLibrary}
          onClose={() => setShowAssetLibrary(false)}
          onSelect={(asset) => {
            setShowAssetLibrary(false);
            toast.success(`Asset "${asset.name}" selected`);
          }}
        />
      )}

      {/* Copy Shared Element Modal */}
      {copyingElement && (
        <CopyToMyElementsModal
          isOpen={true}
          element={copyingElement}
          onClose={handleCloseCopyModal}
          onSuccess={(targetFolderId) => {
            handleCloseCopyModal();
            if (targetFolderId) setFocusFolderId(targetFolderId);
            loadData();
          }}
        />
      )}

      {/* Clone All Shared Elements Modal */}
      {cloningSharedFolder && (
        <CloneAllSharedElementsModal
          isOpen={true}
          sharedFolder={cloningSharedFolder}
          onClose={handleCloseCloneAllModal}
          onSuccess={(targetFolderId) => {
            handleCloseCloneAllModal();
            if (targetFolderId) setFocusFolderId(targetFolderId);
            loadData();
          }}
        />
      )}

      {/* Share Element Modal */}
      {sharingElement && (
        <ShareElementModal
          isOpen={true}
          element={sharingElement}
          onClose={handleCloseShareModal}
        />
      )}

      {/* Share Folder Modal — same flow as Share Element but copies the
          whole folder + every element inside it on import */}
      {sharingFolder && (
        <ShareFolderModal
          isOpen={true}
          folder={sharingFolder}
          onClose={handleCloseShareFolderModal}
        />
      )}

      {/* Get Shared Element Modal */}
      {showGetSharedModal && (
        <GetSharedElementModal
          isOpen={true}
          onClose={handleCloseGetShared}
          onSuccess={handleGetSharedSuccess}
        />
      )}
    </div>
  );
}
